import React, { useState } from 'react';
import { useLanguage } from '../../context/LanguageContext';

const labels = {
  en: {
    heading: 'Newsletter',
    placeholder: 'your@email.com',
    submit: 'Subscribe',
    submitting: 'Subscribing…',
    success: 'Thanks! Check your inbox for occasional Natuvia updates.',
    invalid: 'Please enter a valid email.',
    error: 'Subscription failed. Please try again.',
    rate: 'Please wait a moment before trying again.',
  },
  'zh-hk': {
    heading: '訂閱電子報',
    placeholder: 'your@email.com',
    submit: '訂閱',
    submitting: '訂閱中…',
    success: '多謝訂閱！我們會不時將 Natuvia 的更新發送至你的郵箱。',
    invalid: '請輸入有效的電郵地址。',
    error: '訂閱失敗，請稍後再試。',
    rate: '操作太頻密，請稍候。',
  },
} as const;

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const NewsletterSignup: React.FC = () => {
  const { language } = useLanguage();
  const t = labels[language as keyof typeof labels] ?? labels.en;
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [message, setMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!EMAIL_RE.test(email.trim())) {
      setStatus('error');
      setMessage(t.invalid);
      return;
    }
    setStatus('submitting');
    setMessage('');
    try {
      const res = await fetch('/api/newsletter/subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim(), language, source: 'footer' }),
      });
      if (res.status === 429) {
        setStatus('error');
        setMessage(t.rate);
        return;
      }
      if (!res.ok) {
        setStatus('error');
        setMessage(t.error);
        return;
      }
      setStatus('success');
      setMessage(t.success);
      setEmail('');
    } catch {
      setStatus('error');
      setMessage(t.error);
    }
  };

  if (status === 'success') {
    return (
      <div className="space-y-2" role="status" aria-live="polite">
        <h4 className="text-sm font-bold uppercase tracking-wider mb-3">{t.heading}</h4>
        <div className="flex items-start gap-2 px-4 py-3 rounded-md bg-natuvia-cta/20 border border-natuvia-cta/40">
          <span className="text-natuvia-cta text-xl leading-none" aria-hidden="true">
            ✓
          </span>
          <p className="text-sm text-white">{message || t.success}</p>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-2" noValidate>
      <h4 className="text-sm font-bold uppercase tracking-wider mb-3">{t.heading}</h4>
      <div className="flex gap-2">
        <label htmlFor="newsletter-email" className="sr-only">
          {t.placeholder}
        </label>
        <input
          id="newsletter-email"
          type="email"
          value={email}
          onChange={e => setEmail(e.target.value)}
          placeholder={t.placeholder}
          required
          autoComplete="email"
          aria-invalid={status === 'error'}
          aria-describedby={message ? 'newsletter-message' : undefined}
          className="flex-1 px-3 py-2 rounded-md text-natuvia-text text-sm bg-white/90 placeholder:text-natuvia-text/50 focus:outline-none focus:ring-2 focus:ring-white disabled:opacity-70"
          disabled={status === 'submitting'}
        />
        <button
          type="submit"
          className="px-4 py-2 rounded-md text-sm font-bold bg-natuvia-cta text-white hover:bg-natuvia-cta/90 disabled:opacity-70 disabled:cursor-wait inline-flex items-center gap-2 whitespace-nowrap"
          disabled={status === 'submitting'}
        >
          {status === 'submitting' && (
            <span
              className="inline-block w-3.5 h-3.5 border-2 border-white/40 border-t-white rounded-full animate-spin"
              aria-hidden="true"
            />
          )}
          {status === 'submitting' ? t.submitting : t.submit}
        </button>
      </div>
      {message && (
        <p
          id="newsletter-message"
          role={status === 'error' ? 'alert' : 'status'}
          aria-live="polite"
          className={`text-sm font-medium ${status === 'error' ? 'text-red-200' : 'text-white/90'}`}
        >
          {message}
        </p>
      )}
    </form>
  );
};
